import { useSnackbar } from "notistack";
import {
  jobMessage,
  jobMessageList,
  updateMessageSeen,
} from "../endpoints/job.service";
import { EnumUserTypes, userTypes } from "../utils/constants";
import dayjs from "@lib/dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  fetchJobMessagesProfile,
  fetchUnseenMessageCount,
} from "../store/actions/message.action";
import { useSocket } from "context/SocketProvider";

const LIMIT = 50;

const useChat = ({ jobApplicationId, receiverDetails, fetchInbox = false }) => {
  const dispatch = useDispatch();
  const socketRef = useSocket();
  const socket = socketRef?.current;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state) => state.auth);
  const [page, setPage] = useState(1);
  const [messages, setMessages] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isMessageLoading, setIsMessageLoading] = useState(true);
  const [isOlderMessageLoading, setIsOlderMessageLoading] = useState(false);

  console.log("💬 useChat: Initialized", {
    jobApplicationId,
    receiverDetails: {
      id: receiverDetails?._id,
      userId: receiverDetails?.userId,
      userType: receiverDetails?.userType,
      name: receiverDetails?.firstName + " " + receiverDetails?.lastName,
    },
    currentUser: {
      id: user?._id,
      userType: user?.userType,
      name: user?.firstName + " " + user?.lastName,
    },
    socketConnected: socket?.connected,
    socketId: socket?.id,
  });

  async function loadOlderMessages() {
    setIsOlderMessageLoading(true);
    jobMessageList({
      receiverId: receiverDetails?.userId || receiverDetails?._id,
      jobApplicationId,
      limit: LIMIT,
      page: page + 1,
    })
      .then((response) => {
        console.log("✅ useChat: Message list fetched", {
          count: response?.data?.messages?.length || 0,
          messages: response?.data?.messages?.map((m) => ({
            id: m._id,
            from: m.createdBy,
            message: m.message?.substring(0, 30) + "...",
          })),
        });
        setPage((prev) => prev + 1);
        const olderMessages = response?.data?.messages?.reverse();
        console.log(
          "check older messages",
          olderMessages,
          response?.data?.messages
        );
        setMessages((prev) => {
          return [...olderMessages, ...prev];
        });
        // setMessages(response?.data?.messages || []);
      })
      .catch((error) => {
        console.error("❌ useChat: Failed to fetch older message list", error);
      })
      .finally(() => {
        setIsOlderMessageLoading(false);
      });
  }

  // Send message handler
  async function sendMessage({ message, setMessage }) {
    if (!message.trim() || !receiverDetails) return;

    console.log("📤 useChat: Sending message", {
      message,
      to: receiverDetails?._id,
      toUserId: receiverDetails?.userId,
      jobApplicationId,
      currentUser: user?._id,
    });

    try {
      setIsMessageSending(true);
      let payload = {};

      if (
        user.userType === userTypes.CANDIDATE &&
        receiverDetails.userType === userTypes?.REFERRAL_AMBASSADOR
      ) {
        payload = {
          jobId: id,
          message,
          receivedBy: receiverDetails?._id,
        };
      } else if (user.userType === userTypes?.REFERRAL_AMBASSADOR) {
        payload = {
          jobId: id,
          message,
          receivedBy: receiverDetails?._id,
        };
      } else {
        payload = {
          jobApplicationId: jobApplicationId,
          message,
          receivedBy: receiverDetails?._id,
        };
      }

      console.log("📦 useChat: Message payload", payload);

      const tempMessageId = dayjs().toISOString();

      try {
        socket.emit("tempMessage", {
          tempMessageId,
          message,
          jobApplicationId,
          createdName: user?.firstName + " " + user?.lastName,
          seen: false,
          createdBy: user?._id || "",
          // createdBy:
          //   (user?.userType === EnumUserTypes.CANDIDATE
          //     ? user?.userId
          //     : user?._id) || "",
          isTemp: true,
        });
      } catch (error) {
        console.log(error, "logging here error in temp message emit");
      }

      // // Add temporary message to UI immediately
      // setMessages((prev) => {
      //   return [
      //     ...prev,
      //     {
      //       tempMessageId,
      //       message,
      //       createdName: user?.firstName + " " + user?.lastName,
      //       seen: false,
      //       createdBy: user?._id || "",
      //       isTemp: true,
      //     },
      //   ];
      // });

      setMessage("");

      // Send message to API
      const { success, data, error } = await jobMessage(payload);
      console.log("✅ useChat: Message API response", { success, data, error });

      if (success && data) {
        dispatch(fetchJobMessagesProfile());

        // Replace temporary message with real one
        setMessages((prev) => {
          return prev?.map((item) => {
            if (item?.tempMessageId === tempMessageId) {
              return {
                messageId: data?._id,
                message: data?.message,
                createdName: user?.firstName + " " + user?.lastName,
                createdAt: data?.createdAt,
                seen: data?.seen,
                createdBy: data?.createdBy,
                receivedBy: data?.receivedBy,
              };
            } else {
              return item;
            }
          });
        });

        console.log("🎯 useChat: Message sent successfully", data._id);
      } else {
        // Remove temporary message if failed
        setMessages((prev) =>
          prev.filter((item) => item.tempMessageId !== tempMessageId)
        );
        throw new Error(error || "Failed to send message");
      }
    } catch (error) {
      console.error("❌ useChat: Send message error", error);
      enqueueSnackbar("Error sending message: " + error.message, {
        variant: "error",
      });
    } finally {
      setIsMessageSending(false);
    }
  }

  useEffect(() => {
    if (!socket) return;

    const handleTempMessage = (msg) => {
      const isSameJobApplication = msg?.jobApplicationId === jobApplicationId;
      const isCurrentUsersMessage = msg?.createdBy === user?._id;
      console.log(
        "check temp message",
        msg,
        isSameJobApplication,
        isCurrentUsersMessage
      );

      if (isSameJobApplication && isCurrentUsersMessage) {
        setMessages((prev) => {
          return [
            ...prev,
            {
              tempMessageId: msg.tempMessageId,
              message: msg.message,
              createdName: msg.createdName,
              seen: msg.seen,
              createdBy: msg.createdBy,
              isTemp: msg.isTemp,
            },
          ];
        });
      }
    };

    socket.on("tempMessage", handleTempMessage);

    return () => {
      socket.off("tempMessage", handleTempMessage);
    };
  }, [socket, jobApplicationId]);

  // Socket Listener with comprehensive debugging
  useEffect(() => {
    console.log("🔗 useChat: Setting up socket listeners", {
      socketExists: !!socket,
      socketConnected: socket?.connected,
      socketId: socket?.id,
      jobApplicationId,
      receiverId: receiverDetails?._id,
    });

    if (!socket) {
      console.log("❌ useChat: No socket available - cannot set up listeners");
      return;
    }

    const handleNewMessage = (messageData) => {
      console.log("📨 useChat: newMessage event received", {
        messageId: messageData?._id,
        from: messageData?.createdBy,
        to: messageData?.receivedBy,
        jobAppId: messageData?.jobApplicationId,
        currentJobAppId: jobApplicationId,
        message: messageData?.message?.substring(0, 50) + "...",
      });

      // Always refresh message profile
      dispatch(fetchJobMessagesProfile());

      // 🔥 CRITICAL: Improved message filtering logic
      const isSameJobApplication =
        messageData?.jobApplicationId === jobApplicationId;

      // Determine the correct receiver ID to compare with
      let receiverIdToCompare;
      if (receiverDetails?.userType === userTypes.RECRUITER) {
        receiverIdToCompare = receiverDetails?._id; // Recruiter uses _id
      } else {
        receiverIdToCompare = receiverDetails?.userId; // Others use userId
      }

      const isFromReceiver = messageData?.createdBy === receiverIdToCompare;

      console.log("🔍 useChat: Message filter analysis", {
        isSameJobApplication,
        receiverIdToCompare,
        messageFrom: messageData?.createdBy,
        isFromReceiver,
        shouldDisplay: isSameJobApplication && isFromReceiver,
        receiverDetails: {
          _id: receiverDetails?._id,
          userId: receiverDetails?.userId,
          userType: receiverDetails?.userType,
        },
      });

      if (isSameJobApplication && isFromReceiver) {
        console.log("✅ useChat: Message matches current chat - ADDING TO UI");
        const newMessage = {
          messageId: messageData?._id,
          message: messageData?.message,
          createdName:
            receiverDetails?.firstName + " " + receiverDetails?.lastName,
          createdAt: messageData?.createdAt,
          seen: messageData?.seen,
          createdBy: messageData?.createdBy,
          receivedBy: messageData?.receivedBy,
        };
        setMessages((prev) => [...prev, newMessage]);
      } else {
        console.log("🚫 useChat: Message filtered out - not for this chat");
      }
    };

    const handleMessageRead = (messageResponse) => {
      console.log("👀 useChat: messageReadByReceiver", {
        messageId: messageResponse?._id,
        seen: messageResponse?.seen,
      });

      setMessages((prev) => {
        return prev?.map((item) => {
          if (item?.messageId === messageResponse?._id) {
            console.log(
              "✅ useChat: Marking message as read",
              messageResponse?._id
            );
            return { ...item, seen: true };
          } else {
            return item;
          }
        });
      });
    };

    // Debug socket connection state
    const handleConnect = () => {
      console.log("✅ useChat: Socket connected", socket.id);
    };

    const handleDisconnect = (reason) => {
      console.log("🔌 useChat: Socket disconnected", reason);
    };

    const handleConnectError = (error) => {
      console.error("❌ useChat: Socket connection error", error.message);
    };

    // Remove existing listeners to avoid duplicates
    socket.off("newMessage", handleNewMessage);
    // socket.off("tempMessage", handleNewMessage);
    socket.off("messageReadByReceiver", handleMessageRead);
    socket.off("connect", handleConnect);
    socket.off("disconnect", handleDisconnect);
    socket.off("connect_error", handleConnectError);

    // Add new listeners
    socket.on("newMessage", handleNewMessage);
    // socket.on("tempMessage", handleNewMessage);
    socket.on("messageReadByReceiver", handleMessageRead);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    console.log("✅ useChat: Socket listeners setup complete");

    // Cleanup on component unmount or dependency change
    return () => {
      console.log("🧹 useChat: Cleaning up socket listeners");
      socket.off("newMessage", handleNewMessage);
      socket.off("messageReadByReceiver", handleMessageRead);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [socket, jobApplicationId, receiverDetails, user?._id, dispatch]);

  // Fetching list of messages
  useEffect(() => {
    if (jobApplicationId && receiverDetails) {
      setPage(1);
      console.log("🔄 useChat: Fetching message list", {
        jobApplicationId,
        receiverId: receiverDetails?.userId || receiverDetails?._id,
      });

      setIsMessageLoading(true);
      jobMessageList({
        receiverId: receiverDetails?.userId || receiverDetails?._id,
        jobApplicationId,
        limit: LIMIT,
        page: 1,
      })
        .then((response) => {
          console.log("✅ useChat: Message list fetched", {
            count: response?.data?.messages?.length || 0,
            messages: response?.data?.messages?.map((m) => ({
              id: m._id,
              from: m.createdBy,
              message: m.message?.substring(0, 30) + "...",
            })),
          });
          setMessages(response?.data?.messages?.reverse() || []);
        })
        .catch((error) => {
          console.error("❌ useChat: Failed to fetch message list", error);
        })
        .finally(() => {
          setIsMessageLoading(false);
        });
    } else {
      console.log(
        "🚫 useChat: Skipping message fetch - missing jobApplicationId or receiverDetails"
      );
    }
  }, [receiverDetails, jobApplicationId]);

  // Mark messages as seen
  useEffect(() => {
    if (fetchInbox) {
      console.log("📨 useChat: Fetching inbox messages");
      dispatch(fetchJobMessagesProfile());
      return;
    }

    if (messages?.length > 0) {
      const unseenMessages = messages?.filter(
        (item) =>
          !item?.seen &&
          item?.createdBy !==
            (user?.userType === userTypes.RECRUITER ? user?._id : user?.userId)
      );

      if (unseenMessages.length > 0) {
        console.log("👀 useChat: Marking messages as seen", {
          unseenMessages,
          count: unseenMessages.length,
          messageIds: unseenMessages.map((m) => m.messageId),
        });

        const unseenMessageIds = unseenMessages
          .map((message) => message?._id || message?.messageId)
          .filter((id) => id); // Filter out temporary message IDs

        if (unseenMessageIds.length > 0) {
          updateMessageSeen({ messagesIds: unseenMessageIds })
            .then(() => {
              console.log("✅ useChat: Messages marked as seen");
              dispatch(fetchJobMessagesProfile());
              dispatch(fetchUnseenMessageCount());
            })
            .catch((error) => {
              console.error(
                "❌ useChat: Failed to mark messages as seen",
                error
              );
            });
        }
      }
    }
  }, [messages, fetchInbox, user?.userType, user?._id, user?.userId, dispatch]);

  console.log("💬 useChat: Current state", {
    messageCount: messages.length,
    isLoading: isMessageLoading,
    isSending: isMessageSending,
    hasSocket: !!socket,
    socketConnected: socket?.connected,
  });

  return {
    isMessageLoading,
    messages,
    isMessageSending,
    sendMessage,
    loadOlderMessages,
    isOlderMessageLoading,
  };
};

export default useChat;
